// Structure JSON centralisée pour toutes les questions du formulaire
// Chaque question a un id unique et des propriétés standardisées

import type { UiTranslations } from '@/lib/ui-translations';

// 'message' : un écran sans saisie (titre + texte), affiché selon sa condition
// comme n'importe quelle question ; jamais requis, jamais dans la revue.
export type QuestionType = 'text' | 'date' | 'select' | 'document' | 'travel-history' | 'yes-no' | 'textarea' | 'number' | 'checkbox' | 'address' | 'phone' | 'multi-entry' | 'message';

export interface MultiEntryFieldDef {
  key: string;
  label: string;
  placeholder?: string;
  type: 'text' | 'select' | 'date' | 'yes-no' | 'boolean' | 'number' | 'textarea' | 'document' | 'address';
  options?: { code: string; name: string; nameEn?: string; nameEs?: string }[];
  colSpan?: number; // 1 or 2 out of 2-col grid
  // ── Répétition par personne (voir lib/repeat-groups.ts) ────────────────────
  // Dès qu'une colonne porte `roster`, la liste change de nature : le client ne
  // remplit plus que les colonnes `roster` dans la liste (qui est-ce), puis le
  // questionnaire lui pose chacune des autres colonnes comme une vraie question,
  // une par écran et par personne. Les réponses restent rangées dans la même
  // liste de lignes, pour que la console et le remplissage du PDF ne changent pas.
  roster?: boolean;
  /**
   * Colonne `roster` dont la valeur NOMME la ligne, à la place du prénom et du
   * nom. Toutes les listes à répétition ne décrivent pas des personnes : une
   * occupation se nomme par son titre de poste, un diplôme par son intitulé. Sans
   * cela, le client voyait « Personne 1 » au-dessus des écrans d'un emploi.
   * Aucune colonne marquée = on retombe sur `firstName` puis `lastName`.
   */
  nameKey?: boolean;
  /** Texte de la question posée pour une personne. `{name}` = son prénom. */
  question?: string;
  indication?: string;
  example?: string;
  help?: string;
  whyImportantReason?: string;
  whyImportantConsequence?: string;
  /** Faux = facultatif. Par défaut, suit la question de la liste. */
  required?: boolean;
  /** Condition sur les autres colonnes de LA MÊME personne (OU si tableau). */
  dependsOn?: { key: string; value: any } | Array<{ key: string; value: any }>;
  /** Posée seulement si la personne a au moins / au plus cet âge (date de naissance connue). */
  minAge?: number;
  maxAge?: number;
  /**
   * Colonne `document` lue par une lecture automatique existante (par exemple
   * le passeport). `document` = la clé de la configuration de lecture ; `fields`
   * renomme ses cases vers les colonnes de la ligne (gender → sex) ; `values`
   * traduit une valeur lue vers le code de la colonne (masculin → m_masculin).
   * Les cases que la ligne n'a pas sont ignorées.
   */
  ocr?: { document: string; fields?: Record<string, string>; values?: Record<string, Record<string, string>> };
}

export interface Question {
  id: string;
  type: QuestionType;
  label: string;
  indication: string;
  help?: string;
  example?: string;
  whyImportant?: {
    reason: string;
    consequence?: string;
  };
  placeholder?: string;
  isRequired: boolean;
  requiresConfirmation?: boolean;
  confirmationText?: string;
  successMessage: string;
  options?: { code: string; name: string; nameEn?: string; nameEs?: string }[];
  documentConfig?: {
    acceptedFormats: string[];
    maxSizeMB: number;
    maxFiles?: number;
  };
  validation?: (value: any) => string | null;
  // Conditional display: show when the gate matches. A single condition gates on
  // one parent; an array of conditions is OR (show if ANY matches — e.g. a
  // details box shown when Q1A or Q1B is "oui"). `value` may be an array for a
  // multi-value match on one parent.
  dependsOn?:
    | { questionId: string; value: any }
    | Array<{ questionId: string; value: any }>;
  // Section grouping
  section?: string;
  // Base (French) section name — used for skippedSections matching (language-independent)
  sectionKey?: string;
  // Multi-entry config
  multiEntryFields?: MultiEntryFieldDef[];
  /**
   * Translation key for the "add another" button. When set, the wizard
   * resolves it via getUiTranslations(language) so the label localizes.
   * Preferred over the legacy `multiEntryAddLabel` (kept for backward compat).
   */
  multiEntryAddKey?: keyof UiTranslations;
  multiEntryAddLabel?: string;
  // Optional "please specify" textarea shown below the main input
  detailsBoxLabel?: string;
  // Multilingual translations — keyed by language code (en, fr, ar, es, hi, ja, ko, zh-Hans, zh-Hant)
  translations?: Record<string, string>;
  /** Libellé qui nomme les personnes déjà entrées dans une liste (`{names}`),
   *  par exemple « Vous avez déjà inclus Awa… ». Sans personne, `label` reste. */
  namedLabel?: { list: string; where?: { key: string; value: any }; text: string; textNone?: string };
  /** Écran fabriqué pour une personne d'une liste répétée (lib/repeat-groups.ts). */
  repeatOf?: {
    groupId: string;
    rowId: string;
    key: string;
    rowIndex: number;
    /** Nom de la personne, pour vérifier qu'un document lu est bien le sien. */
    personName?: string;
    ocr?: MultiEntryFieldDef["ocr"];
  };
}

// Liste complète des pays (ISO 3166-1 alpha-2) — noms en français, triée alphabétiquement
export const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'ZA', name: 'Afrique du Sud' },
  { code: 'AL', name: 'Albanie' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'AD', name: 'Andorre' },
  { code: 'AO', name: 'Angola' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AG', name: 'Antigua-et-Barbuda' },
  { code: 'SA', name: 'Arabie Saoudite' },
  { code: 'AR', name: 'Argentine' },
  { code: 'AM', name: 'Arménie' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AU', name: 'Australie' },
  { code: 'AT', name: 'Autriche' },
  { code: 'AZ', name: 'Azerbaïdjan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahreïn' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbade' },
  { code: 'BE', name: 'Belgique' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Bénin' },
  { code: 'BM', name: 'Bermudes' },
  { code: 'BT', name: 'Bhoutan' },
  { code: 'BY', name: 'Biélorussie' },
  { code: 'BO', name: 'Bolivie' },
  { code: 'BA', name: 'Bosnie-Herzégovine' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brésil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgarie' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodge' },
  { code: 'CM', name: 'Cameroun' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cap-Vert' },
  { code: 'CF', name: 'Centrafrique' },
  { code: 'CL', name: 'Chili' },
  { code: 'CN', name: 'Chine' },
  { code: 'CY', name: 'Chypre' },
  { code: 'CO', name: 'Colombie' },
  { code: 'KM', name: 'Comores' },
  { code: 'KP', name: 'Corée du Nord' },
  { code: 'KR', name: 'Corée du Sud' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatie' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'DK', name: 'Danemark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominique' },
  { code: 'EG', name: 'Égypte' },
  { code: 'AE', name: 'Émirats Arabes Unis' },
  { code: 'EC', name: 'Équateur' },
  { code: 'ER', name: 'Érythrée' },
  { code: 'ES', name: 'Espagne' },
  { code: 'EE', name: 'Estonie' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'US', name: 'États-Unis' },
  { code: 'ET', name: 'Éthiopie' },
  { code: 'FJ', name: 'Fidji' },
  { code: 'FI', name: 'Finlande' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambie' },
  { code: 'GE', name: 'Géorgie' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Grèce' },
  { code: 'GD', name: 'Grenade' },
  { code: 'GL', name: 'Groenland' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernesey' },
  { code: 'GN', name: 'Guinée' },
  { code: 'GQ', name: 'Guinée équatoriale' },
  { code: 'GW', name: 'Guinée-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'GF', name: 'Guyane française' },
  { code: 'HT', name: 'Haïti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong (RAS de Chine)' },
  { code: 'HU', name: 'Hongrie' },
  { code: 'CX', name: 'Île Christmas' },
  { code: 'IM', name: 'Île de Man' },
  { code: 'NF', name: 'Île Norfolk' },
  { code: 'AX', name: 'Îles Åland' },
  { code: 'KY', name: 'Îles Caïmans' },
  { code: 'CC', name: 'Îles Cocos' },
  { code: 'CK', name: 'Îles Cook' },
  { code: 'FO', name: 'Îles Féroé' },
  { code: 'FK', name: 'Îles Malouines' },
  { code: 'MP', name: 'Îles Mariannes du Nord' },
  { code: 'PN', name: 'Îles Pitcairn' },
  { code: 'TC', name: 'Îles Turks et Caïques' },
  { code: 'VI', name: 'Îles Vierges américaines' },
  { code: 'VG', name: 'Îles Vierges britanniques' },
  { code: 'IN', name: 'Inde' },
  { code: 'ID', name: 'Indonésie' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Iran' },
  { code: 'IE', name: 'Irlande' },
  { code: 'IS', name: 'Islande' },
  { code: 'IL', name: 'Israël' },
  { code: 'IT', name: 'Italie' },
  { code: 'JM', name: 'Jamaïque' },
  { code: 'JP', name: 'Japon' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordanie' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kirghizistan' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'KW', name: 'Koweït' },
  { code: 'RE', name: 'La Réunion' },
  { code: 'LA', name: 'Laos' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LV', name: 'Lettonie' },
  { code: 'LB', name: 'Liban' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libye' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lituanie' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao (RAS de Chine)' },
  { code: 'MK', name: 'Macédoine du Nord' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MY', name: 'Malaisie' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malte' },
  { code: 'MA', name: 'Maroc' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MU', name: 'Maurice' },
  { code: 'MR', name: 'Mauritanie' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'MX', name: 'Mexique' },
  { code: 'FM', name: 'Micronésie' },
  { code: 'MD', name: 'Moldavie' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolie' },
  { code: 'ME', name: 'Monténégro' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibie' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Népal' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigéria' },
  { code: 'NU', name: 'Niue' },
  { code: 'NO', name: 'Norvège' },
  { code: 'NC', name: 'Nouvelle-Calédonie' },
  { code: 'NZ', name: 'Nouvelle-Zélande' },
  { code: 'OM', name: 'Oman' },
  { code: 'UG', name: 'Ouganda' },
  { code: 'UZ', name: 'Ouzbékistan' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palaos' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papouasie-Nouvelle-Guinée' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'BQ', name: 'Pays-Bas caribéens' },
  { code: 'PE', name: 'Pérou' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Pologne' },
  { code: 'PF', name: 'Polynésie française' },
  { code: 'PR', name: 'Porto Rico' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'CD', name: 'RD Congo' },
  { code: 'DO', name: 'République dominicaine' },
  { code: 'CG', name: 'République du Congo' },
  { code: 'RO', name: 'Roumanie' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'RU', name: 'Russie' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'EH', name: 'Sahara occidental' },
  { code: 'BL', name: 'Saint-Barthélemy' },
  { code: 'KN', name: 'Saint-Kitts-et-Nevis' },
  { code: 'MF', name: 'Saint-Martin (partie française)' },
  { code: 'SX', name: 'Saint-Martin (partie néerlandaise)' },
  { code: 'PM', name: 'Saint-Pierre-et-Miquelon' },
  { code: 'VC', name: 'Saint-Vincent-et-les-Grenadines' },
  { code: 'SH', name: 'Sainte-Hélène' },
  { code: 'LC', name: 'Sainte-Lucie' },
  { code: 'SB', name: 'Salomon' },
  { code: 'SV', name: 'Salvador' },
  { code: 'WS', name: 'Samoa' },
  { code: 'AS', name: 'Samoa américaines' },
  { code: 'ST', name: 'Sao Tomé-et-Principe' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'RS', name: 'Serbie' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapour' },
  { code: 'SK', name: 'Slovaquie' },
  { code: 'SI', name: 'Slovénie' },
  { code: 'SO', name: 'Somalie' },
  { code: 'SD', name: 'Soudan' },
  { code: 'SS', name: 'Soudan du Sud' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SE', name: 'Suède' },
  { code: 'CH', name: 'Suisse' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SJ', name: 'Svalbard et Jan Mayen' },
  { code: 'SY', name: 'Syrie' },
  { code: 'TJ', name: 'Tadjikistan' },
  { code: 'TW', name: 'Taïwan' },
  { code: 'TZ', name: 'Tanzanie' },
  { code: 'TD', name: 'Tchad' },
  { code: 'CZ', name: 'Tchéquie' },
  { code: 'TF', name: 'Terres australes françaises' },
  { code: 'IO', name: 'Territoire britannique de l\'océan Indien' },
  { code: 'TH', name: 'Thaïlande' },
  { code: 'TL', name: 'Timor oriental' },
  { code: 'TG', name: 'Togo' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinité-et-Tobago' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'TM', name: 'Turkménistan' },
  { code: 'TR', name: 'Turquie' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'WF', name: 'Wallis-et-Futuna' },
  { code: 'YE', name: 'Yémen' },
  { code: 'ZM', name: 'Zambie' },
  { code: 'ZW', name: 'Zimbabwe' },
];

const EYE_COLORS = [
  { code: 'brun', name: 'Brun', nameEn: 'Brown', nameEs: 'Marrón' },
  { code: 'bleu', name: 'Bleu', nameEn: 'Blue', nameEs: 'Azul' },
  { code: 'vert', name: 'Vert', nameEn: 'Green', nameEs: 'Verde' },
  { code: 'noisette', name: 'Noisette', nameEn: 'Hazel', nameEs: 'Avellana' },
  { code: 'gris', name: 'Gris', nameEn: 'Gray', nameEs: 'Gris' },
  { code: 'noir', name: 'Noir', nameEn: 'Black', nameEs: 'Negro' },
];

const GENDERS = [
  { code: 'masculin', name: 'Masculin', nameEn: 'Male', nameEs: 'Masculino' },
  { code: 'feminin', name: 'Féminin', nameEn: 'Female', nameEs: 'Femenino' },
  { code: 'autre', name: 'Autre', nameEn: 'Other', nameEs: 'Otro' },
];

const PHONE_TYPES = [
  { code: 'mobile', name: 'Cellulaire / Mobile', nameEn: 'Mobile', nameEs: 'Móvil' },
  { code: 'domicile', name: 'Domicile', nameEn: 'Home', nameEs: 'Casa' },
  { code: 'travail', name: 'Travail', nameEn: 'Work', nameEs: 'Trabajo' },
];

const YES_NO = [
  { code: 'oui', name: 'Oui', nameEn: 'Yes', nameEs: 'Sí' },
  { code: 'non', name: 'Non', nameEn: 'No', nameEs: 'No' },
];

const MARITAL_STATUS = [
  { code: 'celibataire', name: 'Célibataire', nameEn: 'Single', nameEs: 'Soltero(a)' },
  { code: 'marie', name: 'Marié(e)', nameEn: 'Married', nameEs: 'Casado(a)' },
  { code: 'union_civile', name: 'Union civile', nameEn: 'Civil union', nameEs: 'Unión civil' },
  { code: 'conjoint_fait', name: 'Conjoint(e) de fait', nameEn: 'Common-law', nameEs: 'Unión de hecho' },
  { code: 'divorce', name: 'Divorcé(e)', nameEn: 'Divorced', nameEs: 'Divorciado(a)' },
  { code: 'separe', name: 'Séparé(e)', nameEn: 'Separated', nameEs: 'Separado(a)' },
  { code: 'veuf', name: 'Veuf/Veuve', nameEn: 'Widowed', nameEs: 'Viudo(a)' },
];

const LANGUAGES = [
  { code: 'francais', name: 'Français', nameEn: 'French', nameEs: 'Francés' },
  { code: 'anglais', name: 'Anglais', nameEn: 'English', nameEs: 'Inglés' },
  { code: 'les_deux', name: 'Les deux', nameEn: 'Both', nameEs: 'Ambos' },
  { code: 'ni_lun_ni_lautre', name: 'Ni l\'un ni l\'autre', nameEn: 'Neither', nameEs: 'Ninguno' },
];

const SPOUSE_STATUS_CANADA = [
  { code: 'citoyen', name: 'Citoyen(ne) canadien(ne)', nameEn: 'Canadian citizen', nameEs: 'Ciudadano(a) canadiense' },
  { code: 'resident_permanent', name: 'Résident(e) permanent(e)', nameEn: 'Permanent resident', nameEs: 'Residente permanente' },
  { code: 'travailleur_temporaire', name: 'Travailleur temporaire', nameEn: 'Temporary worker', nameEs: 'Trabajador temporal' },
  { code: 'etudiant', name: 'Étudiant(e)', nameEn: 'Student', nameEs: 'Estudiante' },
  { code: 'visiteur', name: 'Visiteur', nameEn: 'Visitor', nameEs: 'Visitante' },
  { code: 'autre', name: 'Autre', nameEn: 'Other', nameEs: 'Otro' },
  { code: 'na', name: 'Non applicable', nameEn: 'Not applicable', nameEs: 'No aplica' },
];

// ===== TOUTES LES QUESTIONS =====
export const QUESTIONS: Record<string, Question> = {

  // ===== SECTION 1: INFORMATIONS PERSONNELLES =====

  // Q1
  fullName: {
    id: 'fullName',
    type: 'text',
    label: 'Quel est votre nom complet ?',
    indication: 'Entrez votre nom complet exactement comme il apparaît sur votre passeport.',
    help: 'Regardez votre passeport en entrant cette information.',
    example: 'Jean-Pierre Dupont, Maria Garcia Rodriguez',
    placeholder: 'Nom complet tel qu\'il apparaît sur votre passeport',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que ceci correspond exactement à mon passeport',
    successMessage: 'Nom complet enregistré',
    section: 'Identification',
    whyImportant: {
      reason: 'Les agents d\'immigration comparent votre nom à votre passeport. C\'est le champ le plus vérifié.',
      consequence: 'Un nom mal orthographié peut retarder votre dossier de plusieurs semaines.',
    },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer votre nom complet';
      if (value.trim().length < 3) return 'Le nom semble trop court';
      return null;
    },
  },

  // Q2
  otherNames: {
    id: 'otherNames',
    type: 'text',
    label: 'Avez-vous d\'autres noms utilisés ?',
    indication: 'Nom de jeune fille, alias, pseudonyme, etc. Si aucun, laissez vide.',
    help: 'Incluez tout nom sous lequel vous avez pu être connu(e) officiellement.',
    example: 'Marie Tremblay (nom de jeune fille)',
    placeholder: 'Autres noms utilisés',
    isRequired: false,
    successMessage: 'Autres noms enregistrés',
    section: 'Identification',
  },

  // Q3
  email: {
    id: 'email',
    type: 'text',
    label: 'Quelle est votre adresse courriel ?',
    indication: 'Cette adresse sera utilisée pour toute communication concernant votre dossier.',
    help: 'Utilisez une adresse que vous consultez régulièrement.',
    example: 'jean.dupont@email.com',
    placeholder: 'votre.email@exemple.com',
    isRequired: true,
    successMessage: 'Courriel enregistré',
    section: 'Coordonnées',
    whyImportant: {
      reason: 'C\'est le principal moyen de communication pour les mises à jour de votre dossier.',
      consequence: 'Un courriel incorrect pourrait vous faire manquer des communications importantes.',
    },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer votre adresse courriel';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Adresse courriel invalide';
      return null;
    },
  },

  // Q4 - Adresse de résidence (tous les champs sur un seul écran)
  residenceAddress: {
    id: 'residenceAddress',
    type: 'address',
    label: 'Quelle est votre adresse de résidence ?',
    indication: 'Entrez votre adresse complète. Les documents officiels seront envoyés à cette adresse.',
    help: 'Remplissez tous les champs obligatoires. L\'appartement est optionnel.',
    isRequired: true,
    successMessage: 'Adresse de résidence enregistrée',
    section: 'Coordonnées',
    whyImportant: {
      reason: 'Les documents officiels et décisions seront envoyés à cette adresse.',
      consequence: 'Mauvaise adresse = documents manqués = demande abandonnée.',
    },
  },

  // Q5
  samePostalAddress: {
    id: 'samePostalAddress',
    type: 'yes-no',
    label: 'Votre adresse postale est-elle la même que l\'adresse de domicile ?',
    indication: 'Indiquez si votre adresse de correspondance est différente de votre adresse de résidence.',
    help: 'Si vous recevez votre courrier à une autre adresse, répondez Non.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Coordonnées',
  },

  // Q6 - Adresse postale (tous les champs sur un seul écran)
  postalAddress: {
    id: 'postalAddress',
    type: 'address',
    label: 'Quelle est votre adresse postale ?',
    indication: 'Entrez l\'adresse où vous recevez votre courrier.',
    help: 'Remplissez tous les champs obligatoires.',
    isRequired: true,
    successMessage: 'Adresse postale enregistrée',
    section: 'Coordonnées',
    dependsOn: { questionId: 'samePostalAddress', value: 'non' },
  },

  // Q7 - Téléphone (indicatif + numéro sur un seul écran)
  phone: {
    id: 'phone',
    type: 'phone',
    label: 'Quel est votre numéro de téléphone ?',
    indication: 'Sélectionnez votre pays et entrez votre numéro de téléphone.',
    isRequired: true,
    successMessage: 'Numéro de téléphone enregistré',
    section: 'Coordonnées',
  },

  // Q8
  phoneType: {
    id: 'phoneType',
    type: 'select',
    label: 'Quel est le type de téléphone ?',
    indication: 'Précisez s\'il s\'agit d\'un téléphone cellulaire, de domicile ou de travail.',
    help: 'Choisissez le type qui correspond au numéro fourni.',
    isRequired: true,
    options: PHONE_TYPES,
    successMessage: 'Type de téléphone enregistré',
    section: 'Coordonnées',
  },

  // Q9
  dateOfBirth: {
    id: 'dateOfBirth',
    type: 'date',
    label: 'Quelle est votre date de naissance ?',
    indication: 'Sélectionnez votre date de naissance. Elle doit correspondre exactement à votre passeport.',
    help: 'Trouvez « Date de naissance » sur votre passeport.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que cette date correspond exactement à mon passeport',
    successMessage: 'Date de naissance enregistrée',
    section: 'Identification',
    whyImportant: {
      reason: 'Votre date de naissance est comparée avec plusieurs bases de données gouvernementales.',
      consequence: 'Une date incorrecte peut signaler votre demande pour vérification manuelle.',
    },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner votre date de naissance';
      return null;
    },
  },

  // Q10
  birthCity: {
    id: 'birthCity',
    type: 'text',
    label: 'Quelle est votre ville de naissance ?',
    indication: 'Entrez la ville exacte telle qu\'elle apparaît sur votre acte de naissance ou passeport.',
    help: 'Vérifiez l\'orthographe sur votre passeport.',
    example: 'Paris, Casablanca, Port-au-Prince',
    placeholder: 'Ville de naissance',
    isRequired: true,
    successMessage: 'Ville de naissance enregistrée',
    section: 'Identification',
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer votre ville de naissance';
      return null;
    },
  },

  // Q11
  birthCountry: {
    id: 'birthCountry',
    type: 'select',
    label: 'Quel est votre pays de naissance ?',
    indication: 'Sélectionnez le pays où vous êtes né(e).',
    help: 'Si le pays a changé de nom, utilisez le nom actuel.',
    isRequired: true,
    options: COUNTRIES,
    successMessage: 'Pays de naissance enregistré',
    section: 'Identification',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner un pays';
      return null;
    },
  },

  // Q12
  eyeColor: {
    id: 'eyeColor',
    type: 'select',
    label: 'Quelle est votre couleur des yeux ?',
    indication: 'Sélectionnez la couleur de vos yeux.',
    help: 'Choisissez la couleur la plus proche.',
    isRequired: true,
    options: EYE_COLORS,
    successMessage: 'Couleur des yeux enregistrée',
    section: 'Identification',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une couleur';
      return null;
    },
  },

  // Q13
  heightCm: {
    id: 'heightCm',
    type: 'number',
    label: 'Quelle est votre taille en centimètres ?',
    indication: 'Entrez votre taille en centimètres.',
    help: 'Si vous connaissez votre taille en pieds, convertissez : 5\'6" = 168 cm.',
    example: '170',
    placeholder: '170',
    isRequired: true,
    successMessage: 'Taille enregistrée',
    section: 'Identification',
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer votre taille';
      const num = parseInt(value);
      if (isNaN(num) || num < 50 || num > 250) return 'Veuillez entrer une taille valide (50-250 cm)';
      return null;
    },
  },

  // Q14
  gender: {
    id: 'gender',
    type: 'select',
    label: 'Quel est votre sexe / genre ?',
    indication: 'Sélectionnez le sexe indiqué sur votre passeport.',
    help: 'Choisissez ce qui correspond à votre document officiel.',
    isRequired: true,
    options: GENDERS,
    successMessage: 'Genre enregistré',
    section: 'Identification',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une option';
      return null;
    },
  },

  // Q15
  idPhoto: {
    id: 'idPhoto',
    type: 'document',
    label: 'Veuillez fournir une photo d\'identité recto-verso récente (3 mois)',
    indication: 'Il ne s\'agit pas d\'un scan de document, mais de la photo de type passeport (comme celle imprimée sur votre passeport) : visage de face, sur fond blanc ou clair, sans chapeau ni lunettes.',
    help: 'C\'est la photo d\'identité officielle (format passeport) qui sera jointe à votre dossier. Elle doit être récente (moins de 3 mois), nette, avec un fond uni.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que cette photo est récente (moins de 3 mois) et conforme',
    successMessage: 'Photo d\'identité téléchargée',
    section: 'Identification',
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
    whyImportant: {
      reason: 'La photo d\'identité est utilisée pour vérifier votre identité visuellement.',
      consequence: 'Une photo floue ou non conforme entraînera un rejet du dossier.',
    },
  },

  // Q16
  nationality: {
    id: 'nationality',
    type: 'select',
    label: 'Quelle est votre nationalité ?',
    indication: 'Sélectionnez votre nationalité principale (pays de citoyenneté).',
    help: 'C\'est généralement le pays qui a émis votre passeport.',
    isRequired: true,
    options: COUNTRIES,
    successMessage: 'Nationalité enregistrée',
    section: 'Identification',
    whyImportant: {
      reason: 'Votre nationalité détermine les exigences de visa applicables.',
      consequence: 'Une nationalité incorrecte pourrait mener à des exigences erronées.',
    },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner votre nationalité';
      return null;
    },
  },

  // Q17
  hasOtherNationality: {
    id: 'hasOtherNationality',
    type: 'yes-no',
    label: 'Avez-vous une autre nationalité ?',
    indication: 'Indiquez si vous possédez la citoyenneté d\'un autre pays.',
    help: 'Double nationalité, nationalité acquise par mariage, etc.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Identification',
  },

  // Q18
  otherNationalities: {
    id: 'otherNationalities',
    type: 'multi-entry',
    label: 'Précisez vos autres nationalités',
    indication: 'Ajoutez chaque nationalité que vous possédez.',
    help: 'Utilisez le bouton + pour ajouter chaque nationalité séparément.',
    isRequired: true,
    successMessage: 'Autres nationalités enregistrées',
    section: 'Identification',
    dependsOn: { questionId: 'hasOtherNationality', value: 'oui' },
    multiEntryFields: [
      { key: 'nationality', label: 'Nationalité', placeholder: 'Sélectionnez un pays', type: 'select', options: COUNTRIES, colSpan: 2 },
    ],
    multiEntryAddKey: 'addNationalityEntry',
  },

  // ===== SECTION 4: ÉTUDES & FORMATION =====

  // Évaluation MIFI
  hasMifiEvaluation: {
    id: 'hasMifiEvaluation',
    type: 'yes-no',
    label: 'Avez-vous une Évaluation comparative des études effectuées hors du Québec (MIFI) ?',
    indication: 'Ce document compare vos diplômes étrangers avec le système éducatif québécois.',
    help: 'Document délivré par le Ministère de l\'Immigration du Québec (MIFI).',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Études & Formation',
  },

  mifiEvaluationDocument: {
    id: 'mifiEvaluationDocument',
    type: 'document',
    label: 'Téléchargez votre Évaluation comparative des études',
    indication: 'Fournissez le document d\'évaluation comparative délivré par le MIFI.',
    help: 'Document officiel du MIFI comparant vos études au système québécois.',
    isRequired: true,
    successMessage: 'Évaluation comparative téléchargée',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasMifiEvaluation', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Aperçu de l'éducation
  educationYears: {
    id: 'educationYears',
    type: 'multi-entry',
    label: 'Pour chaque niveau d\'études, indiquez le nombre d\'années complétées',
    indication: 'Ajoutez une entrée par niveau d\'études complété.',
    help: 'Ajoutez chaque niveau séparément avec le nombre d\'années.',
    isRequired: true,
    successMessage: 'Années d\'études enregistrées',
    section: 'Études & Formation',
    multiEntryFields: [
      { key: 'level', label: 'Niveau d\'études', type: 'select', options: [
        { code: 'primaire', name: 'Primaire', nameEn: 'Primary', nameEs: 'Primaria' },
        { code: 'secondaire', name: 'Secondaire', nameEn: 'Secondary', nameEs: 'Secundaria' },
        { code: 'collegial', name: 'Collégial / Lycée', nameEn: 'College / High school', nameEs: 'Colegial / Bachillerato' },
        { code: 'universitaire_licence', name: 'Universitaire — Licence / Baccalauréat', nameEn: 'University — Bachelor\'s', nameEs: 'Universitario — Licenciatura' },
        { code: 'universitaire_maitrise', name: 'Universitaire — Maîtrise / Master', nameEn: 'University — Master\'s', nameEs: 'Universitario — Maestría' },
        { code: 'universitaire_doctorat', name: 'Universitaire — Doctorat', nameEn: 'University — Doctorate', nameEs: 'Universitario — Doctorado' },
        { code: 'professionnel', name: 'Formation professionnelle', nameEn: 'Vocational training', nameEs: 'Formación profesional' },
        { code: 'autre', name: 'Autre', nameEn: 'Other', nameEs: 'Otro' },
      ], colSpan: 2 },
      { key: 'years', label: 'Nombre d\'années', placeholder: '6', type: 'text' },
    ],
    multiEntryAddKey: 'addEducationEntry',
  },

  highSchoolInfo: {
    id: 'highSchoolInfo',
    type: 'multi-entry',
    label: 'Informations concernant votre école secondaire',
    indication: 'Indiquez les informations de votre école secondaire.',
    help: 'Remplissez chaque champ séparément.',
    isRequired: true,
    successMessage: 'Informations secondaires enregistrées',
    section: 'Études & Formation',
    multiEntryFields: [
      { key: 'schoolName', label: 'Nom de l\'école', placeholder: 'Lycée Victor Hugo', type: 'text', colSpan: 2 },
      { key: 'city', label: 'Ville', placeholder: 'Paris', type: 'text' },
      { key: 'country', label: 'Pays', placeholder: 'France', type: 'select', options: COUNTRIES },
      { key: 'startYear', label: 'Année de début', placeholder: '2005', type: 'text' },
      { key: 'endYear', label: 'Année de fin', placeholder: '2008', type: 'text' },
    ],
    multiEntryAddKey: 'addSecondarySchoolEntry',
  },

  hasHigherDiploma: {
    id: 'hasHigherDiploma',
    type: 'yes-no',
    label: 'Avez-vous un diplôme supérieur aux études secondaires ?',
    indication: 'Indiquez si vous avez obtenu un diplôme post-secondaire (collégial, universitaire, etc.).',
    help: 'DEC, baccalauréat, maîtrise, doctorat, diplôme professionnel, etc.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Études & Formation',
  },

  // Diplôme 1 (obligatoire si études supérieures)
  diploma1Name: {
    id: 'diploma1Name',
    type: 'text',
    label: 'Nom du diplôme 1 (le plus récent)',
    indication: 'Entrez le nom exact de votre diplôme le plus récent.',
    help: 'Ex : Baccalauréat en génie, Maîtrise en droit, DEC en soins infirmiers.',
    example: 'Baccalauréat en administration des affaires',
    placeholder: 'Nom du diplôme',
    isRequired: true,
    successMessage: 'Nom du diplôme enregistré',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le nom du diplôme';
      return null;
    },
  },

  diploma1Field: {
    id: 'diploma1Field',
    type: 'text',
    label: 'Domaine d\'étude (Diplôme 1)',
    indication: 'Le domaine ou la spécialisation de votre diplôme.',
    help: 'Ex : Informatique, Droit, Médecine, Commerce international.',
    example: 'Informatique',
    placeholder: 'Domaine d\'étude',
    isRequired: true,
    successMessage: 'Domaine d\'étude enregistré',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le domaine d\'étude';
      return null;
    },
  },

  diploma1School: {
    id: 'diploma1School',
    type: 'text',
    label: 'Nom de l\'établissement (Diplôme 1)',
    indication: 'Le nom complet de l\'université, collège ou école.',
    help: 'Nom officiel de l\'institution.',
    example: 'Université de Montréal',
    placeholder: 'Nom de l\'établissement',
    isRequired: true,
    successMessage: 'Établissement enregistré',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le nom de l\'établissement';
      return null;
    },
  },

  diploma1Location: {
    id: 'diploma1Location',
    type: 'text',
    label: 'Lieu des études (Diplôme 1)',
    indication: 'Ville et pays où se trouve l\'établissement.',
    help: 'Ex : Montréal, Canada ou Paris, France.',
    example: 'Montréal, Canada',
    placeholder: 'Ville, Pays',
    isRequired: true,
    successMessage: 'Lieu enregistré',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le lieu des études';
      return null;
    },
  },

  diploma1StartDate: {
    id: 'diploma1StartDate',
    type: 'date',
    label: 'Date de début des études (Diplôme 1)',
    indication: 'Sélectionnez la date de début de ce programme.',
    help: 'Date approximative acceptée.',
    isRequired: true,
    successMessage: 'Date de début enregistrée',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de début';
      return null;
    },
  },

  diploma1EndDate: {
    id: 'diploma1EndDate',
    type: 'date',
    label: 'Date de fin des études (Diplôme 1)',
    indication: 'Sélectionnez la date de fin ou de diplomation.',
    help: 'Si en cours, indiquez la date prévue.',
    isRequired: true,
    successMessage: 'Date de fin enregistrée',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de fin';
      return null;
    },
  },

  diploma1Document: {
    id: 'diploma1Document',
    type: 'document',
    label: 'Téléchargez votre Diplôme 1',
    indication: 'Scan ou photo de votre diplôme le plus récent.',
    help: 'Le document officiel délivré par l\'établissement.',
    isRequired: true,
    successMessage: 'Diplôme téléchargé',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  diploma1Transcript: {
    id: 'diploma1Transcript',
    type: 'document',
    label: 'Téléchargez votre relevé de notes (Diplôme 1)',
    indication: 'Fournissez le relevé de notes officiel associé à ce diplôme.',
    help: 'Relevé officiel avec le sceau de l\'établissement.',
    isRequired: false,
    successMessage: 'Relevé de notes téléchargé',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Diplôme 2
  hasMoreDiplomas1: {
    id: 'hasMoreDiplomas1',
    type: 'yes-no',
    label: 'Avez-vous un autre diplôme à déclarer ?',
    indication: 'Indiquez si vous avez un deuxième diplôme post-secondaire.',
    help: 'Vous pouvez déclarer jusqu\'à 6 diplômes au total.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Études & Formation',
    dependsOn: { questionId: 'hasHigherDiploma', value: 'oui' },
  },

  diploma2Name: {
    id: 'diploma2Name', type: 'text',
    label: 'Nom du diplôme 2', indication: 'Nom exact de votre deuxième diplôme.',
    help: 'Ex : Licence, Master, DEC.', placeholder: 'Nom du diplôme',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma2Field: {
    id: 'diploma2Field', type: 'text',
    label: 'Domaine d\'étude (Diplôme 2)', indication: 'Spécialisation du diplôme.',
    help: 'Ex : Informatique, Droit.', placeholder: 'Domaine',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma2School: {
    id: 'diploma2School', type: 'text',
    label: 'Nom de l\'établissement (Diplôme 2)', indication: 'Nom de l\'institution.',
    help: 'Nom officiel.', placeholder: 'Établissement',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma2Location: {
    id: 'diploma2Location', type: 'text',
    label: 'Lieu des études (Diplôme 2)', indication: 'Ville et pays.',
    help: 'Ex : Paris, France.', placeholder: 'Ville, Pays',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma2StartDate: {
    id: 'diploma2StartDate', type: 'date',
    label: 'Date de début (Diplôme 2)', indication: 'Date de début du programme.',
    help: 'Date approximative acceptée.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma2EndDate: {
    id: 'diploma2EndDate', type: 'date',
    label: 'Date de fin (Diplôme 2)', indication: 'Date de fin ou diplomation.',
    help: 'Date prévue si en cours.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma2Document: {
    id: 'diploma2Document', type: 'document',
    label: 'Téléchargez votre Diplôme 2', indication: 'Scan ou photo du diplôme.',
    help: 'Document officiel.',
    isRequired: true, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },
  diploma2Transcript: {
    id: 'diploma2Transcript', type: 'document',
    label: 'Relevé de notes (Diplôme 2)', indication: 'Relevé officiel.',
    help: 'Optionnel.',
    isRequired: false, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Diplôme 3
  hasMoreDiplomas2: {
    id: 'hasMoreDiplomas2', type: 'yes-no',
    label: 'Avez-vous un autre diplôme à déclarer ?',
    indication: 'Indiquez si vous avez un troisième diplôme.', help: 'Jusqu\'à 6 diplômes.',
    isRequired: true, successMessage: 'Réponse enregistrée', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas1', value: 'oui' },
  },
  diploma3Name: {
    id: 'diploma3Name', type: 'text',
    label: 'Nom du diplôme 3', indication: 'Nom exact du diplôme.',
    help: 'Ex : Licence, Master.', placeholder: 'Nom du diplôme',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma3Field: {
    id: 'diploma3Field', type: 'text',
    label: 'Domaine d\'étude (Diplôme 3)', indication: 'Spécialisation.',
    help: 'Ex : Informatique.', placeholder: 'Domaine',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma3School: {
    id: 'diploma3School', type: 'text',
    label: 'Établissement (Diplôme 3)', indication: 'Nom de l\'institution.',
    help: 'Nom officiel.', placeholder: 'Établissement',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma3Location: {
    id: 'diploma3Location', type: 'text',
    label: 'Lieu (Diplôme 3)', indication: 'Ville et pays.',
    help: 'Ex : Montréal, Canada.', placeholder: 'Ville, Pays',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma3StartDate: {
    id: 'diploma3StartDate', type: 'date',
    label: 'Date de début (Diplôme 3)', indication: 'Début du programme.',
    help: 'Approximatif accepté.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma3EndDate: {
    id: 'diploma3EndDate', type: 'date',
    label: 'Date de fin (Diplôme 3)', indication: 'Fin ou diplomation.',
    help: 'Date prévue si en cours.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma3Document: {
    id: 'diploma3Document', type: 'document',
    label: 'Téléchargez votre Diplôme 3', indication: 'Scan ou photo.',
    help: 'Document officiel.',
    isRequired: true, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Diplôme 4
  hasMoreDiplomas3: {
    id: 'hasMoreDiplomas3', type: 'yes-no',
    label: 'Avez-vous un autre diplôme à déclarer ?',
    indication: 'Quatrième diplôme.', help: 'Jusqu\'à 6 diplômes.',
    isRequired: true, successMessage: 'Réponse enregistrée', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas2', value: 'oui' },
  },
  diploma4Name: {
    id: 'diploma4Name', type: 'text',
    label: 'Nom du diplôme 4', indication: 'Nom exact.', help: 'Ex : Doctorat.', placeholder: 'Nom',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma4Field: {
    id: 'diploma4Field', type: 'text',
    label: 'Domaine (Diplôme 4)', indication: 'Spécialisation.', help: 'Domaine.', placeholder: 'Domaine',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma4School: {
    id: 'diploma4School', type: 'text',
    label: 'Établissement (Diplôme 4)', indication: 'Institution.', help: 'Nom officiel.', placeholder: 'Établissement',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma4Location: {
    id: 'diploma4Location', type: 'text',
    label: 'Lieu (Diplôme 4)', indication: 'Ville, pays.', help: 'Lieu.', placeholder: 'Ville, Pays',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma4StartDate: {
    id: 'diploma4StartDate', type: 'date',
    label: 'Date de début (Diplôme 4)', indication: 'Début.', help: 'Approximatif.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma4EndDate: {
    id: 'diploma4EndDate', type: 'date',
    label: 'Date de fin (Diplôme 4)', indication: 'Fin.', help: 'Date prévue si en cours.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma4Document: {
    id: 'diploma4Document', type: 'document',
    label: 'Téléchargez votre Diplôme 4', indication: 'Scan.', help: 'Officiel.',
    isRequired: true, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Diplôme 5
  hasMoreDiplomas4: {
    id: 'hasMoreDiplomas4', type: 'yes-no',
    label: 'Avez-vous un autre diplôme à déclarer ?',
    indication: 'Cinquième diplôme.', help: 'Jusqu\'à 6.',
    isRequired: true, successMessage: 'Réponse enregistrée', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas3', value: 'oui' },
  },
  diploma5Name: {
    id: 'diploma5Name', type: 'text',
    label: 'Nom du diplôme 5', indication: 'Nom exact.', help: 'Diplôme.', placeholder: 'Nom',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma5Field: {
    id: 'diploma5Field', type: 'text',
    label: 'Domaine (Diplôme 5)', indication: 'Spécialisation.', help: 'Domaine.', placeholder: 'Domaine',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma5School: {
    id: 'diploma5School', type: 'text',
    label: 'Établissement (Diplôme 5)', indication: 'Institution.', help: 'Officiel.', placeholder: 'Établissement',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma5Location: {
    id: 'diploma5Location', type: 'text',
    label: 'Lieu (Diplôme 5)', indication: 'Ville, pays.', help: 'Lieu.', placeholder: 'Ville, Pays',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma5StartDate: {
    id: 'diploma5StartDate', type: 'date',
    label: 'Date de début (Diplôme 5)', indication: 'Début.', help: 'Approximatif.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma5EndDate: {
    id: 'diploma5EndDate', type: 'date',
    label: 'Date de fin (Diplôme 5)', indication: 'Fin.', help: 'Date prévue.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma5Document: {
    id: 'diploma5Document', type: 'document',
    label: 'Téléchargez votre Diplôme 5', indication: 'Scan.', help: 'Officiel.',
    isRequired: true, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Diplôme 6
  hasMoreDiplomas5: {
    id: 'hasMoreDiplomas5', type: 'yes-no',
    label: 'Avez-vous un dernier diplôme à déclarer ?',
    indication: 'Sixième et dernier diplôme.', help: 'Maximum 6 diplômes.',
    isRequired: true, successMessage: 'Réponse enregistrée', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas4', value: 'oui' },
  },
  diploma6Name: {
    id: 'diploma6Name', type: 'text',
    label: 'Nom du diplôme 6', indication: 'Nom exact.', help: 'Diplôme.', placeholder: 'Nom',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma6Field: {
    id: 'diploma6Field', type: 'text',
    label: 'Domaine (Diplôme 6)', indication: 'Spécialisation.', help: 'Domaine.', placeholder: 'Domaine',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma6School: {
    id: 'diploma6School', type: 'text',
    label: 'Établissement (Diplôme 6)', indication: 'Institution.', help: 'Officiel.', placeholder: 'Établissement',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma6Location: {
    id: 'diploma6Location', type: 'text',
    label: 'Lieu (Diplôme 6)', indication: 'Ville, pays.', help: 'Lieu.', placeholder: 'Ville, Pays',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Requis' : null,
  },
  diploma6StartDate: {
    id: 'diploma6StartDate', type: 'date',
    label: 'Date de début (Diplôme 6)', indication: 'Début.', help: 'Approximatif.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma6EndDate: {
    id: 'diploma6EndDate', type: 'date',
    label: 'Date de fin (Diplôme 6)', indication: 'Fin.', help: 'Date prévue.',
    isRequired: true, successMessage: 'Enregistré', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Requis' : null,
  },
  diploma6Document: {
    id: 'diploma6Document', type: 'document',
    label: 'Téléchargez votre Diplôme 6', indication: 'Scan.', help: 'Officiel.',
    isRequired: true, successMessage: 'Téléchargé', section: 'Études & Formation',
    dependsOn: { questionId: 'hasMoreDiplomas5', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // ===== SECTION 1 (suite): IDENTIFICATION — PASSEPORT & PIÈCE D'IDENTITÉ =====

  // Q19-a
  passportNumber: {
    id: 'passportNumber',
    type: 'text',
    label: 'Quel est votre numéro de passeport ?',
    indication: 'Entrez le numéro exactement comme il apparaît sur votre passeport.',
    help: 'Trouvez le numéro en haut de la page photo de votre passeport.',
    example: 'AB1234567 ou 123456789',
    placeholder: 'AB1234567',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'J\'ai bien vérifié ce numéro avec mon passeport',
    successMessage: 'Numéro de passeport enregistré',
    section: 'Identification',
    whyImportant: {
      reason: 'Votre numéro de passeport identifie de façon unique votre document de voyage.',
      consequence: 'Un seul caractère erroné peut entraîner un refus de visa.',
    },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer votre numéro de passeport';
      if (value.trim().length < 5) return 'Le numéro de passeport semble trop court';
      return null;
    },
  },

  // Q19-b
  passportCountry: {
    id: 'passportCountry',
    type: 'select',
    label: 'Quel est le pays de délivrance de votre passeport ?',
    indication: 'Sélectionnez le pays qui a émis votre passeport.',
    help: 'Regardez la couverture ou la page d\'information de votre passeport.',
    isRequired: true,
    options: COUNTRIES,
    successMessage: 'Pays de délivrance enregistré',
    section: 'Identification',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner un pays';
      return null;
    },
  },

  // Q19-c
  passportIssueDate: {
    id: 'passportIssueDate',
    type: 'date',
    label: 'Quelle est la date de délivrance de votre passeport ?',
    indication: 'Sélectionnez la date d\'émission indiquée sur votre passeport.',
    help: 'Cherchez « Date of Issue » ou « Date de délivrance » sur la page d\'information.',
    isRequired: true,
    successMessage: 'Date de délivrance enregistrée',
    section: 'Identification',
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de délivrance';
      return null;
    },
  },

  // Q19-d
  passportExpiryDate: {
    id: 'passportExpiryDate',
    type: 'date',
    label: 'Quelle est la date d\'expiration de votre passeport ?',
    indication: 'Sélectionnez la date d\'expiration indiquée sur votre passeport.',
    help: 'Cherchez « Date of Expiry » ou « Date d\'expiration ».',
    isRequired: true,
    successMessage: 'Date d\'expiration enregistrée',
    section: 'Identification',
    whyImportant: {
      reason: 'Votre passeport doit être valide pendant toute la durée de votre séjour.',
      consequence: 'Un passeport expirant bientôt peut entraîner un refus.',
    },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date d\'expiration';
      return null;
    },
  },

  // Q19-e
  passportDocument: {
    id: 'passportDocument',
    type: 'document',
    label: 'Téléchargez votre passeport (recto-verso, photo récente de moins de 3 mois)',
    indication: 'Prenez une photo ou téléchargez un scan recto-verso de votre passeport (page d\'identité et page de signature). La photo doit être nette, sur fond uni, avec le visage bien visible. Tous les coins doivent être visibles.',
    help: 'Photo de type passeport : visage bien visible, fond blanc ou clair, sans reflets. Prenez votre temps — une photo claire évite les retards.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que ce document est récent (moins de 3 mois), complet et lisible',
    successMessage: 'Passeport téléchargé avec succès',
    section: 'Identification',
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
    whyImportant: {
      reason: 'Le scan de passeport sert à la fois de photo d\'identité et de vérification de vos informations.',
      consequence: 'Une photo floue, non conforme ou mal cadrée entraînera un rejet ou un retard du dossier.',
    },
  },

  // Q20-a
  hasNationalId: {
    id: 'hasNationalId',
    type: 'yes-no',
    label: 'Avez-vous une pièce d\'identité nationale ?',
    indication: 'Carte d\'identité émise par votre pays de nationalité (autre que le passeport).',
    help: 'Carte nationale d\'identité, CIN, carte de citoyen, etc.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Identification',
  },

  // Q20-b
  nationalIdNumber: {
    id: 'nationalIdNumber',
    type: 'text',
    label: 'Quel est le numéro de votre pièce d\'identité nationale ?',
    indication: 'Entrez le numéro tel qu\'il apparaît sur votre pièce d\'identité.',
    help: 'Copiez exactement, avec les lettres et chiffres.',
    placeholder: 'Numéro de pièce d\'identité',
    isRequired: true,
    successMessage: 'Numéro d\'identité enregistré',
    section: 'Identification',
    dependsOn: { questionId: 'hasNationalId', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le numéro';
      return null;
    },
  },

  // Q20-c
  nationalIdCountry: {
    id: 'nationalIdCountry',
    type: 'select',
    label: 'Quel est le pays de délivrance de votre pièce d\'identité nationale ?',
    indication: 'Sélectionnez le pays qui a émis votre pièce d\'identité.',
    help: 'C\'est généralement votre pays de nationalité.',
    isRequired: true,
    options: COUNTRIES,
    successMessage: 'Pays de délivrance enregistré',
    section: 'Identification',
    dependsOn: { questionId: 'hasNationalId', value: 'oui' },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner un pays';
      return null;
    },
  },

  // Q20-d1
  nationalIdIssueDate: {
    id: 'nationalIdIssueDate',
    type: 'date',
    label: 'Quelle est la date de délivrance de votre pièce d\'identité nationale ?',
    indication: 'Sélectionnez la date d\'émission de votre pièce d\'identité.',
    help: 'Cherchez « Date de délivrance » sur votre carte d\'identité.',
    isRequired: true,
    successMessage: 'Date de délivrance enregistrée',
    section: 'Identification',
    dependsOn: { questionId: 'hasNationalId', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de délivrance';
      return null;
    },
  },

  // Q20-d2
  nationalIdExpiryDate: {
    id: 'nationalIdExpiryDate',
    type: 'date',
    label: 'Quelle est la date d\'expiration de votre pièce d\'identité nationale ?',
    indication: 'Sélectionnez la date d\'expiration de votre pièce d\'identité.',
    help: 'Cherchez « Date d\'expiration » sur votre carte d\'identité.',
    isRequired: true,
    successMessage: 'Date d\'expiration enregistrée',
    section: 'Identification',
    dependsOn: { questionId: 'hasNationalId', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date d\'expiration';
      return null;
    },
  },

  // Q20-e
  nationalIdDocument: {
    id: 'nationalIdDocument',
    type: 'document',
    label: 'Téléchargez votre pièce d\'identité nationale',
    indication: 'Scan ou photo recto-verso de votre pièce d\'identité nationale.',
    help: 'Assurez-vous que les deux côtés sont lisibles.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que ce document est complet et lisible',
    successMessage: 'Pièce d\'identité téléchargée',
    section: 'Identification',
    dependsOn: { questionId: 'hasNationalId', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // ===== SECTION 6: HISTORIQUE D'IMMIGRATION =====

  // Q21-b (asked first for logical flow)
  hasGreenCard: {
    id: 'hasGreenCard',
    type: 'yes-no',
    label: 'Avez-vous le statut de résident permanent aux États-Unis (carte verte) ?',
    indication: 'Indiquez si vous possédez une carte de résident permanent américain.',
    help: 'Aussi connue sous le nom de « Green Card » ou carte verte.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
  },

  // Q21-c
  greenCardNumber: {
    id: 'greenCardNumber',
    type: 'text',
    label: 'Quel est le numéro de votre carte verte ?',
    indication: 'Entrez le numéro tel qu\'il apparaît sur votre carte de résident permanent.',
    help: 'Le numéro se trouve au dos de la carte.',
    placeholder: 'Numéro de carte verte',
    isRequired: true,
    successMessage: 'Numéro de carte verte enregistré',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasGreenCard', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le numéro';
      return null;
    },
  },

  // Q21-a
  greenCardExpiry: {
    id: 'greenCardExpiry',
    type: 'date',
    label: 'Quelle est la date d\'expiration de votre carte verte ?',
    indication: 'Sélectionnez la date d\'expiration indiquée sur votre carte.',
    help: 'Vérifiez au dos de votre carte de résident permanent.',
    isRequired: true,
    successMessage: 'Date d\'expiration enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasGreenCard', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date d\'expiration';
      return null;
    },
  },

  // Q22
  iucNumber: {
    id: 'iucNumber',
    type: 'text',
    label: 'Numéro d\'identificateur unique client (IUC)',
    indication: 'Si vous avez déjà soumis une demande d\'immigration au Canada, vous pourriez avoir un IUC.',
    help: 'Ce numéro figure sur les correspondances antérieures d\'IRCC. Laissez vide si non applicable.',
    placeholder: 'IUC (si applicable)',
    isRequired: false,
    successMessage: 'IUC enregistré',
    section: 'Historique d\'immigration',
  },

  // Q23
  mifiNumber: {
    id: 'mifiNumber',
    type: 'text',
    label: 'Numéro de référence individuelle (MIFI)',
    indication: 'Si vous avez un numéro de référence du Ministère de l\'Immigration du Québec.',
    help: 'Ce numéro figure sur les correspondances du MIFI. Laissez vide si non applicable.',
    placeholder: 'MIFI (si applicable)',
    isRequired: false,
    successMessage: 'MIFI enregistré',
    section: 'Historique d\'immigration',
  },

  // Q24
  hasBiometrics: {
    id: 'hasBiometrics',
    type: 'yes-no',
    label: 'Avez-vous déjà fourni des données biométriques (Canada) dans les 10 dernières années ?',
    indication: 'Empreintes digitales et photo prises dans un centre de collecte biométrique.',
    help: 'Si vous avez déjà demandé un visa ou permis canadien, vous les avez peut-être déjà fournies.',
    isRequired: false,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
  },

  // Q25
  hasBeenRefused: {
    id: 'hasBeenRefused',
    type: 'yes-no',
    label: 'Vous a-t-on déjà refusé un visa ou un permis, interdit l\'entrée, ou demandé de quitter le Canada ou tout autre pays ?',
    indication: 'Incluez tout refus, interdiction d\'entrée ou mesure de renvoi, dans n\'importe quel pays.',
    help: 'Soyez honnête — les agents vérifient ces informations dans les bases de données internationales.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
    whyImportant: {
      reason: 'Les refus antérieurs sont vérifiés dans les bases de données partagées entre pays.',
      consequence: 'Omettre un refus passé est considéré comme une fausse déclaration et peut entraîner une interdiction.',
    },
  },

  // Q26-a
  hasMedicalExam: {
    id: 'hasMedicalExam',
    type: 'yes-no',
    label: 'Avez-vous effectué un examen médical auprès d\'un médecin désigné (IRCC) au cours des 12 derniers mois ?',
    indication: 'Examen médical réalisé par un médecin de la liste des médecins désignés par IRCC.',
    help: 'Cet examen est différent d\'un examen médical régulier.',
    isRequired: false,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
  },

  // Q26-b
  medicalExamDocument: {
    id: 'medicalExamDocument',
    type: 'document',
    label: 'Téléchargez une copie de votre visite médicale',
    indication: 'Fournissez le document ou reçu de votre examen médical IRCC.',
    help: 'Le document fourni par le médecin désigné après l\'examen.',
    isRequired: true,
    successMessage: 'Document médical téléchargé',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasMedicalExam', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // ===== SECTION 7: ÉTAT MATRIMONIAL & FAMILLE =====

  // Q27-a
  maritalStatus: {
    id: 'maritalStatus',
    type: 'select',
    label: 'Quel est votre état matrimonial actuel ?',
    indication: 'Sélectionnez votre situation familiale actuelle.',
    help: 'Choisissez ce qui correspond à votre statut légal actuel.',
    isRequired: true,
    options: MARITAL_STATUS,
    successMessage: 'État matrimonial enregistré',
    section: 'État matrimonial & Famille',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner votre état matrimonial';
      return null;
    },
  },

  // Q27-b
  marriageCertificate: {
    id: 'marriageCertificate',
    type: 'document',
    label: 'Téléchargez votre acte de mariage ou d\'union civile',
    indication: 'Fournissez une copie de votre acte de mariage ou certificat d\'union civile.',
    help: 'Document officiel délivré par l\'autorité compétente.',
    isRequired: true,
    successMessage: 'Acte de mariage téléchargé',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Q27-c
  marriageDate: {
    id: 'marriageDate',
    type: 'date',
    label: 'Quelle est la date du mariage, de l\'union civile ou de la relation de fait ?',
    indication: 'Sélectionnez la date de début de votre union actuelle.',
    help: 'Date officielle du mariage ou début de la cohabitation.',
    isRequired: true,
    successMessage: 'Date de mariage enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date';
      return null;
    },
  },

  // Q27-d
  spouseFullName: {
    id: 'spouseFullName',
    type: 'text',
    label: 'Quel est le nom complet de votre conjoint(e) ?',
    indication: 'Entrez le nom complet tel qu\'il apparaît sur ses documents officiels.',
    help: 'Nom de famille et prénom(s) complets.',
    placeholder: 'Nom complet du conjoint(e)',
    isRequired: true,
    successMessage: 'Nom du conjoint enregistré',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le nom de votre conjoint(e)';
      return null;
    },
  },

  // Q27-e
  spouseOtherNames: {
    id: 'spouseOtherNames',
    type: 'text',
    label: 'Autres noms utilisés par votre conjoint(e) actuel(le)',
    indication: 'Nom de jeune fille, alias, etc. Laissez vide si aucun.',
    help: 'Tout nom sous lequel votre conjoint(e) a été connu(e).',
    placeholder: 'Autres noms du conjoint(e)',
    isRequired: false,
    successMessage: 'Autres noms enregistrés',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
  },

  // Q27-f
  spousePassport: {
    id: 'spousePassport',
    type: 'document',
    label: 'Téléchargez le passeport de votre conjoint(e)',
    indication: 'Scan ou photo de la page d\'identité du passeport de votre conjoint(e).',
    help: 'La page avec la photo et les informations personnelles.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que ce document est complet et lisible',
    successMessage: 'Passeport du conjoint téléchargé',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Q27-g
  spouseGender: {
    id: 'spouseGender',
    type: 'select',
    label: 'Quel est le sexe / genre de votre conjoint(e) ?',
    indication: 'Sélectionnez le sexe tel qu\'indiqué sur le passeport de votre conjoint(e).',
    help: 'Référez-vous au passeport de votre conjoint(e).',
    isRequired: true,
    options: GENDERS,
    successMessage: 'Genre du conjoint enregistré',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une option';
      return null;
    },
  },

  // Q27-h
  bothPresentAtCeremony: {
    id: 'bothPresentAtCeremony',
    type: 'yes-no',
    label: 'Étiez-vous tous les deux présents physiquement lors de la cérémonie ?',
    indication: 'Indiquez si vous et votre conjoint(e) étiez physiquement présents au mariage.',
    help: 'Mariage par procuration = Non.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
  },

  // Q27-i
  spouseStatusCanada: {
    id: 'spouseStatusCanada',
    type: 'select',
    label: 'Quel est le statut de votre conjoint(e) au Canada ?',
    indication: 'Sélectionnez le statut d\'immigration actuel de votre conjoint(e) au Canada.',
    help: 'Si votre conjoint(e) n\'est pas au Canada, sélectionnez « Non applicable ».',
    isRequired: true,
    options: SPOUSE_STATUS_CANADA,
    successMessage: 'Statut du conjoint enregistré',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une option';
      return null;
    },
  },

  // Q27-j
  spouseDateOfBirth: {
    id: 'spouseDateOfBirth',
    type: 'date',
    label: 'Quelle est la date de naissance de votre conjoint(e) ?',
    indication: 'Sélectionnez la date de naissance de votre conjoint(e).',
    help: 'Vérifiez sur le passeport de votre conjoint(e).',
    isRequired: true,
    successMessage: 'Date de naissance du conjoint enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de naissance';
      return null;
    },
  },

  // Q27-k - Adresse du conjoint (tous les champs sur un seul écran)
  spouseAddress: {
    id: 'spouseAddress',
    type: 'address',
    label: 'Adresse de résidence de votre conjoint(e)',
    indication: 'Entrez l\'adresse complète de votre conjoint(e) ou époux(se).',
    help: 'Si vous vivez ensemble, indiquez la même adresse.',
    isRequired: true,
    successMessage: 'Adresse du conjoint enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
  },

  // Q27-l
  spouseOccupation: {
    id: 'spouseOccupation',
    type: 'text',
    label: 'Quelle est la profession actuelle de votre conjoint(e) ?',
    indication: 'Indiquez l\'emploi ou la profession actuelle de votre conjoint(e).',
    help: 'Si sans emploi, indiquez « Sans emploi » ou « Au foyer ».',
    example: 'Ingénieur, Enseignant, Médecin',
    placeholder: 'Profession du conjoint(e)',
    isRequired: true,
    successMessage: 'Profession du conjoint enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer la profession';
      return null;
    },
  },

  // Q27-m
  spouseStatusProof: {
    id: 'spouseStatusProof',
    type: 'document',
    label: 'Fournissez une preuve de statut de votre conjoint(e) au Canada',
    indication: 'Permis de travail, permis d\'études, carte de résident permanent, etc.',
    help: 'Optionnel si votre conjoint(e) n\'est pas au Canada.',
    isRequired: false,
    successMessage: 'Preuve de statut téléchargée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'maritalStatus', value: 'marie' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Q28-a
  wasPreviouslyMarried: {
    id: 'wasPreviouslyMarried',
    type: 'yes-no',
    label: 'Avez-vous déjà été marié(e) ou en union de fait auparavant ?',
    indication: 'Indiquez si vous avez eu un mariage ou une union de fait avant votre situation actuelle.',
    help: 'Incluez les mariages terminés par divorce, annulation ou décès.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'État matrimonial & Famille',
  },

  // Q28-b
  divorceDocument: {
    id: 'divorceDocument',
    type: 'document',
    label: 'Téléchargez l\'acte de divorce',
    indication: 'Fournissez le jugement de divorce ou la preuve de dissolution de l\'union.',
    help: 'Document officiel du tribunal ou de l\'autorité compétente.',
    isRequired: true,
    successMessage: 'Acte de divorce téléchargé',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'wasPreviouslyMarried', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Q28-c
  exSpouseName: {
    id: 'exSpouseName',
    type: 'text',
    label: 'Quel est le nom complet de votre ex-conjoint(e) ?',
    indication: 'Nom complet tel qu\'il figurait sur les documents officiels.',
    help: 'Nom de famille et prénom(s) complets.',
    placeholder: 'Nom complet de l\'ex-conjoint(e)',
    isRequired: true,
    successMessage: 'Nom de l\'ex-conjoint enregistré',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'wasPreviouslyMarried', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez entrer le nom';
      return null;
    },
  },

  // Q28-d
  exSpouseDob: {
    id: 'exSpouseDob',
    type: 'date',
    label: 'Quelle est la date de naissance de votre ex-conjoint(e) ?',
    indication: 'Sélectionnez la date de naissance de votre ex-conjoint(e).',
    help: 'Date approximative acceptée si vous ne connaissez pas la date exacte.',
    isRequired: true,
    successMessage: 'Date de naissance de l\'ex-conjoint enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'wasPreviouslyMarried', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date';
      return null;
    },
  },

  // Q28-e1
  exSpouseUnionStartDate: {
    id: 'exSpouseUnionStartDate',
    type: 'date',
    label: 'Quelle était la date de début de l\'union avec votre ex-conjoint(e) ?',
    indication: 'Sélectionnez la date de début de votre union précédente.',
    help: 'Date du mariage ou du début de la cohabitation.',
    isRequired: true,
    successMessage: 'Date de début de l\'union enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'wasPreviouslyMarried', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de début';
      return null;
    },
  },

  // Q28-e2
  exSpouseUnionEndDate: {
    id: 'exSpouseUnionEndDate',
    type: 'date',
    label: 'Quelle était la date de fin de l\'union avec votre ex-conjoint(e) ?',
    indication: 'Sélectionnez la date de fin de votre union précédente.',
    help: 'Date du divorce ou de la séparation.',
    isRequired: true,
    successMessage: 'Date de fin de l\'union enregistrée',
    section: 'État matrimonial & Famille',
    dependsOn: { questionId: 'wasPreviouslyMarried', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date de fin';
      return null;
    },
  },

  // ===== SECTION 3: RÉSIDENCE & LANGUES =====

  // Q29
  currentResidenceCountry: {
    id: 'currentResidenceCountry',
    type: 'select',
    label: 'Quel est votre pays de résidence actuel ?',
    indication: 'Le pays où vous vivez actuellement.',
    help: 'Le pays où vous résidez la majeure partie du temps.',
    isRequired: true,
    options: COUNTRIES,
    successMessage: 'Pays de résidence enregistré',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner un pays';
      return null;
    },
  },

  // Q30
  residenceStatus: {
    id: 'residenceStatus',
    type: 'text',
    label: 'Quel est votre statut dans le pays de résidence actuel ?',
    indication: 'Citoyen, résident permanent, résident temporaire, étudiant, etc.',
    help: 'Indiquez le statut légal que vous détenez dans votre pays de résidence.',
    example: 'Citoyen, Résident permanent, Titulaire d\'un visa de travail',
    placeholder: 'Votre statut',
    isRequired: true,
    successMessage: 'Statut de résidence enregistré',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez préciser votre statut';
      return null;
    },
  },

  // Q31
  residenceStatusDocument: {
    id: 'residenceStatusDocument',
    type: 'document',
    label: 'Téléchargez le document attestant de votre statut dans votre pays de résidence',
    indication: 'Fournissez une preuve de votre statut actuel (carte de résident, visa, permis, etc.).',
    help: 'Document officiel montrant votre droit de résider dans le pays.',
    isRequired: true,
    successMessage: 'Document de statut téléchargé',
    section: 'Résidence & Langues',
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // Q32
  residenceStatusExpiry: {
    id: 'residenceStatusExpiry',
    type: 'date',
    label: 'Quelle est la date de validité de votre statut dans le pays de résidence ?',
    indication: 'Date d\'expiration de votre permis, visa ou statut temporaire.',
    help: 'Si vous êtes citoyen, vous pouvez passer cette question.',
    isRequired: false,
    successMessage: 'Date de validité enregistrée',
    section: 'Résidence & Langues',
  },

  // Q33-a
  intendedCity: {
    id: 'intendedCity',
    type: 'text',
    label: 'Dans quelle ville prévoyez-vous résider au Canada ?',
    indication: 'Indiquez la ville où vous prévoyez habiter.',
    help: 'Ex : Montréal, Toronto, Vancouver.',
    example: 'Montréal',
    placeholder: 'Ville',
    isRequired: true,
    successMessage: 'Ville enregistrée',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez préciser la ville';
      return null;
    },
  },

  // Q33-b
  intendedProvince: {
    id: 'intendedProvince',
    type: 'select',
    label: 'Dans quelle province ou territoire prévoyez-vous résider ?',
    indication: 'Sélectionnez la province ou le territoire.',
    help: 'La province où se situe la ville indiquée.',
    isRequired: true,
    options: [
      { code: 'AB', name: 'Alberta', nameEn: 'Alberta', nameEs: 'Alberta' },
      { code: 'BC', name: 'Colombie-Britannique', nameEn: 'British Columbia', nameEs: 'Columbia Británica' },
      { code: 'MB', name: 'Manitoba', nameEn: 'Manitoba', nameEs: 'Manitoba' },
      { code: 'NB', name: 'Nouveau-Brunswick', nameEn: 'New Brunswick', nameEs: 'Nuevo Brunswick' },
      { code: 'NL', name: 'Terre-Neuve-et-Labrador', nameEn: 'Newfoundland and Labrador', nameEs: 'Terranova y Labrador' },
      { code: 'NS', name: 'Nouvelle-Écosse', nameEn: 'Nova Scotia', nameEs: 'Nueva Escocia' },
      { code: 'NT', name: 'Territoires du Nord-Ouest', nameEn: 'Northwest Territories', nameEs: 'Territorios del Noroeste' },
      { code: 'NU', name: 'Nunavut', nameEn: 'Nunavut', nameEs: 'Nunavut' },
      { code: 'ON', name: 'Ontario', nameEn: 'Ontario', nameEs: 'Ontario' },
      { code: 'PE', name: 'Île-du-Prince-Édouard', nameEn: 'Prince Edward Island', nameEs: 'Isla del Príncipe Eduardo' },
      { code: 'QC', name: 'Québec', nameEn: 'Quebec', nameEs: 'Quebec' },
      { code: 'SK', name: 'Saskatchewan', nameEn: 'Saskatchewan', nameEs: 'Saskatchewan' },
      { code: 'YT', name: 'Yukon', nameEn: 'Yukon', nameEs: 'Yukón' },
    ],
    successMessage: 'Province enregistrée',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une province';
      return null;
    },
  },

  // Q34
  livedOtherCountries: {
    id: 'livedOtherCountries',
    type: 'yes-no',
    label: 'Avez-vous résidé dans un autre pays dans les 5 dernières années pendant plus de 6 mois ?',
    indication: 'Tout séjour de plus de 6 mois consécutifs dans un pays autre que votre pays de résidence actuel.',
    help: 'Études à l\'étranger, missions de travail, etc.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Résidence & Langues',
  },

  // Q35
  otherCountriesDetails: {
    id: 'otherCountriesDetails',
    type: 'multi-entry',
    label: 'Précisez les pays, statuts et périodes de résidence',
    indication: 'Pour chaque pays, indiquez le pays, votre statut et la période.',
    help: 'Ajoutez une entrée par pays de résidence.',
    isRequired: true,
    successMessage: 'Détails de résidence enregistrés',
    section: 'Résidence & Langues',
    dependsOn: { questionId: 'livedOtherCountries', value: 'oui' },
    multiEntryFields: [
      { key: 'country', label: 'Pays', placeholder: 'France', type: 'select', options: COUNTRIES, colSpan: 2 },
      { key: 'status', label: 'Statut', placeholder: 'Étudiant, Travailleur...', type: 'text' },
      { key: 'startDate', label: 'Date de début', placeholder: 'MM/AAAA', type: 'text' },
      { key: 'endDate', label: 'Date de fin', placeholder: 'MM/AAAA', type: 'text' },
    ],
    multiEntryAddKey: 'addResidenceEntry',
  },

  // Q36
  motherLanguage: {
    id: 'motherLanguage',
    type: 'text',
    label: 'Quelle est votre langue maternelle ?',
    indication: 'La langue que vous avez apprise en premier dans l\'enfance.',
    help: 'La langue parlée à la maison pendant votre enfance.',
    example: 'Français, Arabe, Créole',
    placeholder: 'Langue maternelle',
    isRequired: true,
    successMessage: 'Langue maternelle enregistrée',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez indiquer votre langue maternelle';
      return null;
    },
  },

  // Q37
  speaksOtherLanguages: {
    id: 'speaksOtherLanguages',
    type: 'yes-no',
    label: 'Parlez-vous une autre langue ?',
    indication: 'Indiquez si vous parlez d\'autres langues en plus de votre langue maternelle.',
    help: 'Même un niveau de base compte.',
    isRequired: false,
    successMessage: 'Réponse enregistrée',
    section: 'Résidence & Langues',
  },

  // Q38
  otherLanguages: {
    id: 'otherLanguages',
    type: 'multi-entry',
    label: 'Listez les autres langues parlées',
    indication: 'Ajoutez chaque langue que vous parlez.',
    help: 'Utilisez le bouton + pour ajouter chaque langue séparément.',
    isRequired: true,
    successMessage: 'Langues enregistrées',
    section: 'Résidence & Langues',
    dependsOn: { questionId: 'speaksOtherLanguages', value: 'oui' },
    multiEntryFields: [
      { key: 'language', label: 'Langue', placeholder: 'Anglais', type: 'text', colSpan: 2 },
    ],
    multiEntryAddKey: 'addLanguageEntry',
  },

  // Q39
  canCommunicateFrEn: {
    id: 'canCommunicateFrEn',
    type: 'select',
    label: 'Pouvez-vous communiquer en français, en anglais ou dans les deux ?',
    indication: 'Sélectionnez votre capacité de communication dans les langues officielles du Canada.',
    help: 'Ceci concerne votre capacité à communiquer au quotidien.',
    isRequired: true,
    options: LANGUAGES,
    successMessage: 'Capacité linguistique enregistrée',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une option';
      return null;
    },
  },

  // Q40
  preferredLanguage: {
    id: 'preferredLanguage',
    type: 'select',
    label: 'Dans quelle langue êtes-vous le plus à l\'aise ?',
    indication: 'Sélectionnez la langue dans laquelle vous préférez communiquer.',
    help: 'Les communications officielles pourront être dans cette langue.',
    isRequired: true,
    options: [
      { code: 'francais', name: 'Français', nameEn: 'French', nameEs: 'Francés' },
      { code: 'anglais', name: 'Anglais', nameEn: 'English', nameEs: 'Inglés' },
    ],
    successMessage: 'Langue préférée enregistrée',
    section: 'Résidence & Langues',
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner une langue';
      return null;
    },
  },

  // Q41
  hasLanguageTest: {
    id: 'hasLanguageTest',
    type: 'yes-no',
    label: 'Avez-vous fait évaluer vos compétences linguistiques par un organisme reconnu ?',
    indication: 'TCF, TEF, IELTS, CELPIP ou autre test reconnu par IRCC.',
    help: 'Tests de français : TCF Canada, TEF Canada. Tests d\'anglais : IELTS, CELPIP.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Résidence & Langues',
  },

  // Q42
  languageTestDocument: {
    id: 'languageTestDocument',
    type: 'document',
    label: 'Téléchargez votre preuve d\'évaluation linguistique',
    indication: 'Fournissez les résultats de votre test de langue reconnu.',
    help: 'Les résultats doivent être datés de moins de 2 ans.',
    isRequired: true,
    successMessage: 'Résultats linguistiques téléchargés',
    section: 'Résidence & Langues',
    dependsOn: { questionId: 'hasLanguageTest', value: 'oui' },
    documentConfig: {
      acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMB: 10,
    },
  },

  // ===== SECTION 6 (suite): VOYAGES ANTÉRIEURS AU CANADA =====

  // Q43
  hasEnteredCanada: {
    id: 'hasEnteredCanada',
    type: 'yes-no',
    label: 'Êtes-vous déjà entré(e) au Canada ?',
    indication: 'Indiquez si vous avez déjà voyagé ou séjourné au Canada.',
    help: 'Incluez tout type de visite : tourisme, études, travail, transit.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
  },

  // Q44
  firstEntryDate: {
    id: 'firstEntryDate',
    type: 'date',
    label: 'Quelle est la date de votre première entrée au Canada ?',
    indication: 'Sélectionnez la date de votre toute première entrée sur le territoire canadien.',
    help: 'Date approximative acceptée si vous ne vous souvenez pas de la date exacte.',
    isRequired: true,
    successMessage: 'Date de première entrée enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasEnteredCanada', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date';
      return null;
    },
  },

  // Q45
  firstEntryPlace: {
    id: 'firstEntryPlace',
    type: 'select',
    label: 'Quel était le lieu de votre première entrée au Canada ?',
    indication: 'Sélectionnez le point d\'entrée par lequel vous êtes entré(e) au Canada.',
    help: 'Aéroport, poste frontalier ou port d\'entrée.',
    isRequired: true,
    options: [
      { code: 'montreal_trudeau', name: 'Aéroport Montréal-Trudeau (YUL)', nameEn: 'Montréal-Trudeau Airport (YUL)', nameEs: 'Aeropuerto Montréal-Trudeau (YUL)' },
      { code: 'toronto_pearson', name: 'Aéroport Toronto Pearson (YYZ)', nameEn: 'Toronto Pearson Airport (YYZ)', nameEs: 'Aeropuerto Toronto Pearson (YYZ)' },
      { code: 'vancouver_yvr', name: 'Aéroport Vancouver (YVR)', nameEn: 'Vancouver Airport (YVR)', nameEs: 'Aeropuerto Vancouver (YVR)' },
      { code: 'calgary_yyc', name: 'Aéroport Calgary (YYC)', nameEn: 'Calgary Airport (YYC)', nameEs: 'Aeropuerto Calgary (YYC)' },
      { code: 'ottawa_yow', name: 'Aéroport Ottawa (YOW)', nameEn: 'Ottawa Airport (YOW)', nameEs: 'Aeropuerto Ottawa (YOW)' },
      { code: 'quebec_yqb', name: 'Aéroport Québec Jean-Lesage (YQB)', nameEn: 'Québec Jean-Lesage Airport (YQB)', nameEs: 'Aeropuerto Québec Jean-Lesage (YQB)' },
      { code: 'edmonton_yeg', name: 'Aéroport Edmonton (YEG)', nameEn: 'Edmonton Airport (YEG)', nameEs: 'Aeropuerto Edmonton (YEG)' },
      { code: 'winnipeg_ywg', name: 'Aéroport Winnipeg (YWG)', nameEn: 'Winnipeg Airport (YWG)', nameEs: 'Aeropuerto Winnipeg (YWG)' },
      { code: 'halifax_yhz', name: 'Aéroport Halifax (YHZ)', nameEn: 'Halifax Airport (YHZ)', nameEs: 'Aeropuerto Halifax (YHZ)' },
      { code: 'lacolle', name: 'Poste frontalier de Lacolle', nameEn: 'Lacolle border crossing', nameEs: 'Puesto fronterizo de Lacolle' },
      { code: 'stanstead', name: 'Poste frontalier de Stanstead', nameEn: 'Stanstead border crossing', nameEs: 'Puesto fronterizo de Stanstead' },
      { code: 'niagara', name: 'Poste frontalier de Niagara Falls', nameEn: 'Niagara Falls border crossing', nameEs: 'Puesto fronterizo de Niagara Falls' },
      { code: 'pacific_highway', name: 'Poste frontalier Pacific Highway', nameEn: 'Pacific Highway border crossing', nameEs: 'Puesto fronterizo Pacific Highway' },
      { code: 'autre', name: 'Autre', nameEn: 'Other', nameEs: 'Otro' },
    ],
    successMessage: 'Lieu de première entrée enregistré',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasEnteredCanada', value: 'oui' },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner le lieu d\'entrée';
      return null;
    },
  },

  // Q46
  firstEntryReason: {
    id: 'firstEntryReason',
    type: 'text',
    label: 'Quelle était la raison de votre première venue au Canada ?',
    indication: 'Motif de votre premier voyage au Canada.',
    help: 'Tourisme, études, travail, regroupement familial, etc.',
    example: 'Tourisme, Études universitaires, Travail temporaire',
    placeholder: 'Raison de la venue',
    isRequired: true,
    successMessage: 'Raison enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasEnteredCanada', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez préciser la raison';
      return null;
    },
  },

  // Q47
  hasLeftAndReturned: {
    id: 'hasLeftAndReturned',
    type: 'yes-no',
    label: 'Avez-vous quitté le Canada et y êtes-vous revenu(e) depuis votre première entrée ?',
    indication: 'Indiquez si vous avez fait des allers-retours depuis votre premier séjour.',
    help: 'Si vous avez fait plusieurs voyages au Canada.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasEnteredCanada', value: 'oui' },
  },

  // Q48
  lastEntryDate: {
    id: 'lastEntryDate',
    type: 'date',
    label: 'Quelle est la date de votre dernière entrée au Canada ?',
    indication: 'Date de votre entrée la plus récente au Canada.',
    help: 'Vérifiez le tampon dans votre passeport.',
    isRequired: true,
    successMessage: 'Date de dernière entrée enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasLeftAndReturned', value: 'oui' },
    validation: (value: Date | null) => {
      if (!value) return 'Veuillez sélectionner la date';
      return null;
    },
  },

  // Q49
  lastEntryPlace: {
    id: 'lastEntryPlace',
    type: 'select',
    label: 'Quel était le lieu de votre dernière entrée au Canada ?',
    indication: 'Sélectionnez le point d\'entrée de votre entrée la plus récente.',
    help: 'Aéroport, poste frontalier ou port d\'entrée.',
    isRequired: true,
    options: [
      { code: 'montreal_trudeau', name: 'Aéroport Montréal-Trudeau (YUL)', nameEn: 'Montréal-Trudeau Airport (YUL)', nameEs: 'Aeropuerto Montréal-Trudeau (YUL)' },
      { code: 'toronto_pearson', name: 'Aéroport Toronto Pearson (YYZ)', nameEn: 'Toronto Pearson Airport (YYZ)', nameEs: 'Aeropuerto Toronto Pearson (YYZ)' },
      { code: 'vancouver_yvr', name: 'Aéroport Vancouver (YVR)', nameEn: 'Vancouver Airport (YVR)', nameEs: 'Aeropuerto Vancouver (YVR)' },
      { code: 'calgary_yyc', name: 'Aéroport Calgary (YYC)', nameEn: 'Calgary Airport (YYC)', nameEs: 'Aeropuerto Calgary (YYC)' },
      { code: 'ottawa_yow', name: 'Aéroport Ottawa (YOW)', nameEn: 'Ottawa Airport (YOW)', nameEs: 'Aeropuerto Ottawa (YOW)' },
      { code: 'quebec_yqb', name: 'Aéroport Québec Jean-Lesage (YQB)', nameEn: 'Québec Jean-Lesage Airport (YQB)', nameEs: 'Aeropuerto Québec Jean-Lesage (YQB)' },
      { code: 'edmonton_yeg', name: 'Aéroport Edmonton (YEG)', nameEn: 'Edmonton Airport (YEG)', nameEs: 'Aeropuerto Edmonton (YEG)' },
      { code: 'winnipeg_ywg', name: 'Aéroport Winnipeg (YWG)', nameEn: 'Winnipeg Airport (YWG)', nameEs: 'Aeropuerto Winnipeg (YWG)' },
      { code: 'halifax_yhz', name: 'Aéroport Halifax (YHZ)', nameEn: 'Halifax Airport (YHZ)', nameEs: 'Aeropuerto Halifax (YHZ)' },
      { code: 'lacolle', name: 'Poste frontalier de Lacolle', nameEn: 'Lacolle border crossing', nameEs: 'Puesto fronterizo de Lacolle' },
      { code: 'stanstead', name: 'Poste frontalier de Stanstead', nameEn: 'Stanstead border crossing', nameEs: 'Puesto fronterizo de Stanstead' },
      { code: 'niagara', name: 'Poste frontalier de Niagara Falls', nameEn: 'Niagara Falls border crossing', nameEs: 'Puesto fronterizo de Niagara Falls' },
      { code: 'pacific_highway', name: 'Poste frontalier Pacific Highway', nameEn: 'Pacific Highway border crossing', nameEs: 'Puesto fronterizo Pacific Highway' },
      { code: 'autre', name: 'Autre', nameEn: 'Other', nameEs: 'Otro' },
    ],
    successMessage: 'Lieu de dernière entrée enregistré',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasLeftAndReturned', value: 'oui' },
    validation: (value: string) => {
      if (!value) return 'Veuillez sélectionner le lieu d\'entrée';
      return null;
    },
  },

  // Q50
  lastEntryReason: {
    id: 'lastEntryReason',
    type: 'text',
    label: 'Quelle était la raison de votre dernière entrée au Canada ?',
    indication: 'Motif de votre dernier voyage au Canada.',
    help: 'Tourisme, études, travail, regroupement familial, etc.',
    placeholder: 'Raison de la venue',
    isRequired: true,
    successMessage: 'Raison enregistrée',
    section: 'Historique d\'immigration',
    dependsOn: { questionId: 'hasLeftAndReturned', value: 'oui' },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez préciser la raison';
      return null;
    },
  },

  // ===== SECTION 5: EMPLOI =====

  // Emp-1
  hasCurrentEmployment: {
    id: 'hasCurrentEmployment',
    type: 'yes-no',
    label: 'Avez-vous actuellement un emploi ?',
    indication: 'Indiquez si vous occupez actuellement un poste rémunéré.',
    help: 'Incluez tout emploi actuel, même à temps partiel.',
    isRequired: true,
    successMessage: 'Réponse enregistrée',
    section: 'Emploi',
  },

  // Emp-2a
  empJobTitle: {
    id: 'empJobTitle',
    type: 'text',
    label: 'Quel est votre titre de poste actuel ?',
    indication: 'Entrez votre titre officiel tel qu\'il apparaît sur votre contrat ou lettre d\'emploi.',
    help: 'Ex : Développeur logiciel, Infirmier(ère), Comptable.',
    example: 'Développeur logiciel senior',
    placeholder: 'Titre du poste',
    isRequired: true,
    successMessage: 'Titre de poste enregistré',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Veuillez entrer votre titre de poste' : null,
  },

  // Emp-2b
  empStartDate: {
    id: 'empStartDate',
    type: 'date',
    label: 'Quelle est la date de début de votre emploi actuel ?',
    indication: 'Sélectionnez la date à laquelle vous avez commencé ce poste.',
    help: 'Date approximative acceptée.',
    isRequired: true,
    successMessage: 'Date de début enregistrée',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: Date | null) => !v ? 'Veuillez sélectionner la date de début' : null,
  },

  // Emp-2c
  empCompanyName: {
    id: 'empCompanyName',
    type: 'text',
    label: 'Quel est le nom de votre entreprise ?',
    indication: 'Nom légal ou commercial de votre employeur actuel.',
    help: 'Nom officiel tel qu\'il apparaît sur vos bulletins de paie.',
    example: 'Desjardins, Google Canada, CHU de Montréal',
    placeholder: 'Nom de l\'entreprise',
    isRequired: true,
    successMessage: 'Nom de l\'entreprise enregistré',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Veuillez entrer le nom de l\'entreprise' : null,
  },

  // Emp-2d
  empCompanyAddress: {
    id: 'empCompanyAddress',
    type: 'text',
    label: 'Quelle est l\'adresse de l\'entreprise ?',
    indication: 'Ville et pays de votre lieu de travail.',
    help: 'Indiquez la ville et le pays du bureau principal.',
    example: 'Montréal, Canada',
    placeholder: 'Ville, Pays',
    isRequired: true,
    successMessage: 'Adresse de l\'entreprise enregistrée',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Veuillez entrer l\'adresse de l\'entreprise' : null,
  },

  // Emp-2e
  empFullOrPartTime: {
    id: 'empFullOrPartTime',
    type: 'select',
    label: 'S\'agit-il d\'un emploi à temps plein ou à temps partiel ?',
    indication: 'Sélectionnez le type d\'emploi.',
    help: 'Temps plein = généralement 30h ou plus par semaine.',
    isRequired: true,
    options: [
      { code: 'temps_plein', name: 'Temps plein', nameEn: 'Full-time', nameEs: 'Tiempo completo' },
      { code: 'temps_partiel', name: 'Temps partiel', nameEn: 'Part-time', nameEs: 'Tiempo parcial' },
    ],
    successMessage: 'Type d\'emploi enregistré',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => !v ? 'Veuillez sélectionner une option' : null,
  },

  // Emp-2f
  empHoursPerWeek: {
    id: 'empHoursPerWeek',
    type: 'number',
    label: 'Combien d\'heures travaillez-vous par semaine ?',
    indication: 'Entrez le nombre moyen d\'heures travaillées par semaine.',
    help: 'Nombre approximatif si variable.',
    example: '40',
    placeholder: '40',
    isRequired: true,
    successMessage: 'Heures par semaine enregistrées',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => {
      if (!v?.trim()) return 'Veuillez entrer le nombre d\'heures';
      const num = parseInt(v);
      if (isNaN(num) || num < 1 || num > 80) return 'Veuillez entrer un nombre valide (1-80)';
      return null;
    },
  },

  // Emp-2g
  empMainDuties: {
    id: 'empMainDuties',
    type: 'textarea',
    label: 'Quelles sont vos principales tâches et responsabilités ?',
    indication: 'Décrivez brièvement vos fonctions principales dans ce poste.',
    help: 'Listez 3 à 5 responsabilités clés.',
    example: 'Développement d\'applications web, gestion de bases de données, supervision d\'une équipe de 3 personnes',
    placeholder: 'Décrivez vos principales tâches...',
    isRequired: true,
    successMessage: 'Tâches enregistrées',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    validation: (v: string) => !v?.trim() ? 'Veuillez décrire vos tâches' : null,
  },

  // Emp-2h
  empReferenceLetter: {
    id: 'empReferenceLetter',
    type: 'document',
    label: 'Téléchargez une lettre de référence de votre employeur actuel',
    indication: 'Lettre de votre employeur confirmant votre poste, tâches et durée d\'emploi.',
    help: 'La lettre doit être sur papier en-tête de l\'entreprise et signée.',
    isRequired: false,
    successMessage: 'Lettre de référence téléchargée',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Emp-2i
  empAttestationCurrent: {
    id: 'empAttestationCurrent',
    type: 'document',
    label: 'Téléchargez une attestation d\'emploi récente',
    indication: 'Attestation de votre employeur actuel confirmant votre emploi.',
    help: 'Document récent (moins de 3 mois) avec le titre de poste et la date d\'embauche.',
    isRequired: false,
    successMessage: 'Attestation d\'emploi téléchargée',
    section: 'Emploi',
    dependsOn: { questionId: 'hasCurrentEmployment', value: 'oui' },
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
  },

  // Emp-3
  empAttestationsPast: {
    id: 'empAttestationsPast',
    type: 'document',
    label: 'Téléchargez les attestations d\'emploi relatives à vos expériences de travail pertinentes',
    indication: 'Fournissez les attestations ou lettres de référence de vos emplois précédents pertinents.',
    help: 'Ajoutez une attestation par emploi pertinent.',
    isRequired: true,
    successMessage: 'Attestations téléchargées',
    section: 'Emploi',
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10, maxFiles: 5 },
  },

  // Emp-4
  empHistory: {
    id: 'empHistory',
    type: 'multi-entry',
    label: 'Décrivez votre historique des emplois',
    indication: 'Pour chaque emploi précédent, indiquez le titre du poste, le nom de l\'entreprise, la ville/pays et les dates.',
    help: 'Ajoutez une entrée par emploi précédent.',
    isRequired: true,
    successMessage: 'Historique des emplois enregistré',
    section: 'Emploi',
    multiEntryFields: [
      { key: 'title', label: 'Titre du poste', placeholder: 'Analyste junior', type: 'text', colSpan: 2 },
      { key: 'company', label: 'Entreprise', placeholder: 'Banque Nationale', type: 'text' },
      { key: 'location', label: 'Ville, Pays', placeholder: 'Montréal, Canada', type: 'text' },
      { key: 'startDate', label: 'Date de début', placeholder: 'MM/AAAA', type: 'text' },
      { key: 'endDate', label: 'Date de fin', placeholder: 'MM/AAAA ou En cours', type: 'text' },
      { key: 'duties', label: 'Tâches principales', placeholder: 'Analyse financière, rapports...', type: 'text', colSpan: 2 },
    ],
    multiEntryAddKey: 'addJobEntry',
  },

  // Emp-5
  empResume: {
    id: 'empResume',
    type: 'document',
    label: 'Téléchargez votre curriculum vitae',
    indication: 'Fournissez votre CV à jour en format PDF.',
    help: 'Assurez-vous que votre CV est récent et inclut toutes vos expériences pertinentes.',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je confirme que ce CV est à jour et complet',
    successMessage: 'CV téléchargé',
    section: 'Emploi',
    documentConfig: { acceptedFormats: ['image/jpeg', 'image/png', 'application/pdf'], maxSizeMB: 10 },
    whyImportant: {
      reason: 'Votre CV est un document central pour évaluer votre profil professionnel.',
      consequence: 'Un CV incomplet ou obsolète peut affecter l\'évaluation de votre dossier.',
    },
  },

  // Emp-6 (Consent - accuracy for employment)
  empConsentAccuracy: {
    id: 'empConsentAccuracy',
    type: 'checkbox',
    label: 'Déclaration d\'exactitude — Emploi',
    indication: 'Je déclare que toutes les informations relatives à mon emploi fournies dans ce formulaire sont exactes et complètes au meilleur de ma connaissance.',
    help: 'Cette déclaration couvre spécifiquement la section emploi.',
    isRequired: true,
    successMessage: 'Déclaration acceptée',
    section: 'Emploi',
  },

  // Emp-7 (Consent - government disclosure for employment)
  empConsentDisclosure: {
    id: 'empConsentDisclosure',
    type: 'checkbox',
    label: 'Consentement à la divulgation — Emploi',
    indication: 'J\'autorise la divulgation de mes informations d\'emploi aux autorités gouvernementales compétentes aux fins du traitement de ma demande.',
    help: 'Vos données d\'emploi pourront être vérifiées auprès des employeurs mentionnés.',
    isRequired: true,
    successMessage: 'Consentement accepté',
    section: 'Emploi',
  },

  // Emp-8 (Signature for employment)
  empSignature: {
    id: 'empSignature',
    type: 'text',
    label: 'Signature électronique — Emploi',
    indication: 'Tapez votre nom complet pour confirmer l\'exactitude des informations d\'emploi.',
    help: 'Votre nom complet tel que saisi au début du formulaire.',
    placeholder: 'Votre nom complet',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je comprends que cette signature engage ma responsabilité quant à l\'exactitude des informations d\'emploi',
    successMessage: 'Signature enregistrée',
    section: 'Emploi',
    validation: (v: string) => {
      if (!v?.trim()) return 'Veuillez signer en tapant votre nom complet';
      if (v.trim().length < 3) return 'La signature semble trop courte';
      return null;
    },
  },

  // ===== SECTION 8: CONSENTEMENT & SIGNATURE =====

  // Q51
  consentAccuracy: {
    id: 'consentAccuracy',
    type: 'checkbox',
    label: 'Déclaration d\'exactitude',
    indication: 'Je déclare que toutes les informations fournies dans ce formulaire sont exactes et complètes au meilleur de ma connaissance.',
    help: 'Cette déclaration a une valeur légale. Assurez-vous d\'avoir vérifié toutes vos réponses.',
    isRequired: true,
    successMessage: 'Déclaration acceptée',
    section: 'Consentement & Signature',
    whyImportant: {
      reason: 'Toute fausse déclaration peut entraîner un refus ou une interdiction future.',
      consequence: 'Les fausses déclarations peuvent mener à une interdiction de territoire de 5 ans.',
    },
  },

  // Q52
  consentPrivacy: {
    id: 'consentPrivacy',
    type: 'checkbox',
    label: 'Consentement de confidentialité',
    indication: 'J\'autorise la collecte, l\'utilisation et la divulgation de mes renseignements personnels aux autorités gouvernementales compétentes aux fins du traitement de ma demande d\'immigration.',
    help: 'Vos données seront traitées conformément aux lois sur la protection de la vie privée.',
    isRequired: true,
    successMessage: 'Consentement accepté',
    section: 'Consentement & Signature',
  },

  // Q53
  signature: {
    id: 'signature',
    type: 'text',
    label: 'Signature électronique',
    indication: 'Tapez votre nom complet en guise de signature électronique. Cela confirme votre identité et votre accord.',
    help: 'Votre nom complet tel que saisi au début du formulaire.',
    placeholder: 'Votre nom complet',
    isRequired: true,
    requiresConfirmation: true,
    confirmationText: 'Je comprends que cette signature a valeur de déclaration officielle',
    successMessage: 'Signature enregistrée',
    section: 'Consentement & Signature',
    whyImportant: {
      reason: 'Votre signature confirme que vous êtes l\'auteur de cette demande.',
      consequence: 'Sans signature, votre demande ne peut pas être traitée.',
    },
    validation: (value: string) => {
      if (!value?.trim()) return 'Veuillez signer en tapant votre nom complet';
      if (value.trim().length < 3) return 'La signature semble trop courte';
      return null;
    },
  },
};

// Ordre des questions dans le flux
export const QUESTION_ORDER: string[] = [
  // Name first — so the client's identity is known before any document is scanned
  'fullName',
  // Passport scan second — OCR fills remaining identity fields automatically
  'passportDocument',
  'otherNames',
  'email',
  'phone',
  'phoneType',
  'residenceAddress',
  'samePostalAddress',
  'postalAddress',
  'dateOfBirth',
  'birthCity',
  'birthCountry',
  'gender',
  'eyeColor',
  'heightCm',
  'idPhoto',
  'nationality',
  'hasOtherNationality',
  'otherNationalities',
  'passportNumber',
  'passportCountry',
  'passportIssueDate',
  'passportExpiryDate',
  'hasNationalId',
  'nationalIdDocument',
  'nationalIdNumber',
  'nationalIdCountry',
  'nationalIdIssueDate',
  'nationalIdExpiryDate',

  // Section 3: Résidence & Langues
  'currentResidenceCountry',
  'residenceStatus',
  'residenceStatusDocument',
  'residenceStatusExpiry',
  'intendedCity',
  'intendedProvince',
  'livedOtherCountries',
  'otherCountriesDetails',
  'motherLanguage',
  'speaksOtherLanguages',
  'otherLanguages',
  'canCommunicateFrEn',
  'preferredLanguage',
  'hasLanguageTest',
  'languageTestDocument',

  // Section 4: Études & Formation
  'hasMifiEvaluation',
  'mifiEvaluationDocument',
  'educationYears',
  'highSchoolInfo',
  'hasHigherDiploma',
  'diploma1Document',
  'diploma1Transcript',
  'diploma1Name',
  'diploma1Field',
  'diploma1School',
  'diploma1Location',
  'diploma1StartDate',
  'diploma1EndDate',
  'hasMoreDiplomas1',
  'diploma2Document',
  'diploma2Transcript',
  'diploma2Name',
  'diploma2Field',
  'diploma2School',
  'diploma2Location',
  'diploma2StartDate',
  'diploma2EndDate',
  'hasMoreDiplomas2',
  'diploma3Document',
  'diploma3Name',
  'diploma3Field',
  'diploma3School',
  'diploma3Location',
  'diploma3StartDate',
  'diploma3EndDate',
  'hasMoreDiplomas3',
  'diploma4Document',
  'diploma4Name',
  'diploma4Field',
  'diploma4School',
  'diploma4Location',
  'diploma4StartDate',
  'diploma4EndDate',
  'hasMoreDiplomas4',
  'diploma5Document',
  'diploma5Name',
  'diploma5Field',
  'diploma5School',
  'diploma5Location',
  'diploma5StartDate',
  'diploma5EndDate',
  'hasMoreDiplomas5',
  'diploma6Document',
  'diploma6Name',
  'diploma6Field',
  'diploma6School',
  'diploma6Location',
  'diploma6StartDate',
  'diploma6EndDate',

  // Section 5: Emploi
  'hasCurrentEmployment',
  'empJobTitle',
  'empStartDate',
  'empCompanyName',
  'empCompanyAddress',
  'empFullOrPartTime',
  'empHoursPerWeek',
  'empMainDuties',
  'empReferenceLetter',
  'empAttestationCurrent',
  'empAttestationsPast',
  'empHistory',
  'empResume',
  'empConsentAccuracy',
  'empConsentDisclosure',
  'empSignature',

  // Section 6: Historique d'immigration
  'hasGreenCard',
  'greenCardNumber',
  'greenCardExpiry',
  'iucNumber',
  'mifiNumber',
  'hasBiometrics',
  'hasBeenRefused',
  'hasMedicalExam',
  'medicalExamDocument',
  'hasEnteredCanada',
  'firstEntryDate',
  'firstEntryPlace',
  'firstEntryReason',
  'hasLeftAndReturned',
  'lastEntryDate',
  'lastEntryPlace',
  'lastEntryReason',

  // Section 7: État matrimonial & Famille
  'maritalStatus',
  'marriageCertificate',
  'marriageDate',
  'spouseFullName',
  'spouseOtherNames',
  'spousePassport',
  'spouseGender',
  'bothPresentAtCeremony',
  'spouseStatusCanada',
  'spouseDateOfBirth',
  'spouseAddress',
  'spouseOccupation',
  'spouseStatusProof',
  'wasPreviouslyMarried',
  'divorceDocument',
  'exSpouseName',
  'exSpouseDob',
  'exSpouseUnionStartDate',
  'exSpouseUnionEndDate',

  // Section 8: Consentement & Signature
  'consentAccuracy',
  'consentPrivacy',
  'signature',
];

// Helper pour obtenir les questions ordonnées (filtre les conditionnelles non applicable)
export function getOrderedQuestions(): Question[] {
  return QUESTION_ORDER.map(id => QUESTIONS[id]).filter(Boolean);
}

// Helper pour obtenir les questions visibles en fonction des réponses
// ocrFilledFields: set of question IDs that were auto-filled by OCR and confirmed — these are skipped
export function getVisibleQuestions(answers: Record<string, any>, ocrFilledFields?: Set<string>): Question[] {
  return getOrderedQuestions().filter(q => {
    // Skip questions already answered by document scans
    if (ocrFilledFields && ocrFilledFields.has(q.id)) return false;
    if (!q.dependsOn) return true;
    // Single condition gates on one parent; an array is OR across parents.
    const conds = Array.isArray(q.dependsOn) ? q.dependsOn : [q.dependsOn];
    return conds.some((c) => {
      const actual = answers[c.questionId];
      return Array.isArray(c.value) ? c.value.includes(actual) : actual === c.value;
    });
  });
}

// Helper pour obtenir une question par ID
export function getQuestionById(id: string): Question | undefined {
  return QUESTIONS[id];
}

// Calcul du temps restant
export function getEstimatedTimeRemaining(currentStep: number, totalSteps: number): string {
  const stepsRemaining = totalSteps - currentStep;
  const minutesPerStep = 0.5;
  const minutesRemaining = Math.ceil(stepsRemaining * minutesPerStep);
  
  if (minutesRemaining <= 1) return 'moins d\'une minute';
  return `environ ${minutesRemaining} minutes`;
}
